import { useState, useEffect, useRef } from 'react';
import { FiX, FiCheck, FiXCircle, FiInfo } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { useFilter } from '../../../contexts/FilterContext';
import { apiCall } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../../utils/permissions';
import { useDemoMode } from '../../../hooks/useDemoMode';
import awsCrossAccountPolicy from './awsCrossAccountPolicy.json';
import awsPayerAccountPolicy from './awsPayerAccountPolicy.json';
import awsPartnerCentralPolicy from './awsPartnerCentralPolicy.json';

interface AWSAccount {
  id: string;
  account_id: string;
  account_type?: string;
  role_name?: string;
  parent_payer_account_id?: string;
  name?: string;
  status?: string;
  client_id?: string;
}

interface AWSEnvironment {
  id: string;
  name: string;
  environment_type?: string;
  description?: string;
  track_credits?: boolean;
  account_id?: string;
  aws_account_id?: string;
  client_id?: string;
  client_name?: string;
  aws_account?: AWSAccount;
}

interface TestResultData {
  success: boolean;
  message?: string;
  details?: string;
  account_id?: string;
}

const AWSEnvironments = () => {
  const { user } = useAuth();
  const { selectedClientId, accessibleClients } = useClient();
  const { refreshFilterData } = useFilter();
  const { hasPermission } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const { isDemoMode, isOnboarding } = useDemoMode();
  const [environments, setEnvironments] = useState<AWSEnvironment[]>([]);
  const [accounts, setAccounts] = useState<AWSAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showAccountOnlyWizard, setShowAccountOnlyWizard] = useState(false);
  const [aiviloAccountId, setAiviloAccountId] = useState('');

  // AWS users have read-only access
  const isReadOnly = isAWSUser();
  const canActOnClients =
    hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
    hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
    hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
    hasPermission(PERMISSIONS.PARTNERS_MANAGE);
  const isPartner =
    hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
    hasPermission(PERMISSIONS.PARTNERS_MANAGE);

  // Wizard state - Partners now skip client selection since it's global
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedAccount, setSelectedAccount] = useState<AWSAccount | null>(null);
  const [newAccountId, setNewAccountId] = useState('');
  const [roleName, setRoleName] = useState('AiviloLensCrossAccountRole');
  const [accountType, setAccountType] = useState('standard');
  const [parentPayerAccountId, setParentPayerAccountId] = useState('');
  const [environmentName, setEnvironmentName] = useState('');
  const [environmentType, setEnvironmentType] = useState('');
  const [environmentDescription, setEnvironmentDescription] = useState('');
  const [trackCredits, setTrackCredits] = useState(false);
  const [testResult, setTestResult] = useState<TestResultData | null>(null);
  const [testing, setTesting] = useState(false);
  const testResultRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (testResult) {
      testResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [testResult]);

  // Edit account state
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [editPayerAccountId, setEditPayerAccountId] = useState('');

  // Edit environment state
  const [editingEnvironmentId, setEditingEnvironmentId] = useState<string | null>(null);
  const [editEnvName, setEditEnvName] = useState('');
  const [editEnvType, setEditEnvType] = useState('');
  const [editEnvDescription, setEditEnvDescription] = useState('');
  const [editEnvTrackCredits, setEditEnvTrackCredits] = useState(false);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const clientParam = selectedClientId ? `?client_id=${selectedClientId}` : '';
      const [envsResponse, accountsResponse, accountIdResponse] = await Promise.all([
        apiCall(`/aws/environments${clientParam}`),
        apiCall(`/aws/accounts${clientParam}`),
        apiCall('/aws/aivilo-account-id')
      ]);

      setEnvironments(envsResponse);
      setAccounts(accountsResponse);
      setAiviloAccountId(accountIdResponse.account_id);

      // Debug: Log accounts to see their structure
    } catch (error) {
      console.error('Error loading AWS environments:', error);
    } finally {
      setLoading(false);
    }
  };

  const testAccountAccess = async () => {
    try {
      setTesting(true);
      setTestResult(null);

      const params: Record<string, string> = {
        account_id: newAccountId,
        role_name: roleName
      };

      // For partners, include the selected client ID
      if (canActOnClients && selectedClientId) {
        params.client_id = selectedClientId;
      }

      const queryParams = new URLSearchParams(params).toString();

      const response = await apiCall(`/aws/accounts/test?${queryParams}`, {
        method: 'POST'
      });

      setTestResult(response);
    } catch (error) {
      setTestResult({
        success: false,
        message: (error instanceof Error ? error.message : '') ||'Error testing account access'
      });
    } finally {
      setTesting(false);
    }
  };

  const createAccount = async () => {
    try {
      const payload: Record<string, unknown> = {
        account_id: newAccountId,
        role_name: roleName,
        account_type: accountType
      };

      // Add parent payer account if selected
      if (parentPayerAccountId) {
        payload.parent_payer_account_id = parentPayerAccountId;
      }

      // For partner accounts, add partner_id
      if (accountType === 'partner' && user?.partner_id) {
        payload.partner_id = user?.partner_id;
      }

      // For partners, include the selected client ID
      if (isPartner && selectedClientId) {
        payload.client_id = selectedClientId;
      }

      const response = await apiCall('/aws/accounts', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setAccounts([...accounts, response]);
      setSelectedAccount(response);
      refreshFilterData();

      // For account-only flow, or payer/partner accounts, close and finish
      if (showAccountOnlyWizard || accountType === 'payer' || accountType === 'partner') {
        setShowWizard(false);
        setShowAccountOnlyWizard(false);
        resetWizard();
      } else {
        setWizardStep(3);
      }
    } catch (error) {
      alert((error instanceof Error ? error.message : '') ||'Error creating account');
    }
  };

  const createEnvironment = async () => {
    try {
      const accountId = selectedAccount?.id;
      if (!accountId) {
        alert('No account selected');
        return;
      }

      const environmentData: Record<string, unknown> = {
        name: environmentName,
        description: environmentDescription,
        environment_type: environmentType,
        track_credits: trackCredits,
        aws_account_id: accountId
      };

      // Add client_id for partners
      if (isPartner && selectedClientId) {
        environmentData.client_id = selectedClientId;
      }

      const response = await apiCall('/aws/environments', {
        method: 'POST',
        body: JSON.stringify(environmentData)
      });

      setEnvironments([...environments, response]);
      refreshFilterData();
      setShowWizard(false);
      resetWizard();
    } catch (error) {
      alert((error instanceof Error ? error.message : '') ||'Error creating environment');
    }
  };

  const resetWizard = () => {
    setWizardStep(1); // All users start at account selection
    setSelectedAccount(null);
    setNewAccountId('');
    setRoleName('AiviloLensCrossAccountRole');
    setAccountType('standard');
    setParentPayerAccountId('');
    setEnvironmentName('');
    setEnvironmentType('');
    setEnvironmentDescription('');
    setTrackCredits(false);
    setTestResult(null);
  };

  const deleteEnvironment = async (environmentId: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this environment? This action cannot be undone.')) {
      return;
    }

    try {
      await apiCall(`/aws/environments/${environmentId}`, {
        method: 'DELETE'
      });

      setEnvironments(environments.filter(env => env.id !== environmentId));
      refreshFilterData();
    } catch (error) {
      alert((error instanceof Error ? error.message : '') ||'Error deleting environment');
    }
  };

  const deleteAccount = async (accountId: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this AWS account? This action cannot be undone.')) {
      return;
    }

    try {
      await apiCall(`/aws/accounts/${accountId}`, {
        method: 'DELETE'
      });

      setAccounts(accounts.filter(acc => acc.id !== accountId));
      // Also remove any environments using this account
      setEnvironments(environments.filter(env => env.aws_account_id !== accountId));
      refreshFilterData();
    } catch (error) {
      alert((error instanceof Error ? error.message : '') ||'Error deleting account');
    }
  };

  const startEditAccount = (account: AWSAccount) => {
    setEditingAccountId(account.id);
    setEditPayerAccountId(account.parent_payer_account_id || '');
  };

  const cancelEditAccount = () => {
    setEditingAccountId(null);
    setEditPayerAccountId('');
  };

  const updateAccount = async (accountId: string) => {
    try {
      const payload: Record<string, unknown> = {
        role_name: accounts.find((a: AWSAccount) => a.id === accountId)?.role_name // Keep existing role name
      };

      // Only include parent_payer_account_id if it's set (even if empty string to clear it)
      if (editPayerAccountId !== undefined) {
        payload.parent_payer_account_id = editPayerAccountId || null;
      }

      const response = await apiCall(`/aws/accounts/${accountId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      // Update the account in state
      setAccounts(accounts.map(acc => acc.id === accountId ? response : acc));
      setEditingAccountId(null);
      setEditPayerAccountId('');
    } catch (error) {
      alert((error instanceof Error ? error.message : '') ||'Error updating account');
    }
  };

  const startEditEnvironment = (env: AWSEnvironment) => {
    setEditingEnvironmentId(env.id);
    setEditEnvName(env.name);
    setEditEnvType(env.environment_type || '');
    setEditEnvDescription(env.description || '');
    setEditEnvTrackCredits(env.track_credits || false);
  };

  const cancelEditEnvironment = () => {
    setEditingEnvironmentId(null);
    setEditEnvName('');
    setEditEnvType('');
    setEditEnvDescription('');
    setEditEnvTrackCredits(false);
  };

  const updateEnvironment = async (environmentId: string) => {
    try {
      const payload = {
        name: editEnvName,
        environment_type: editEnvType || null,
        description: editEnvDescription || null,
        track_credits: editEnvTrackCredits
      };

      const response = await apiCall(`/aws/environments/${environmentId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      // Update the environment in state
      setEnvironments(environments.map(env => env.id === environmentId ? response : env));
      setEditingEnvironmentId(null);
      setEditEnvName('');
      setEditEnvType('');
      setEditEnvDescription('');
      setEditEnvTrackCredits(false);
    } catch (error) {
      alert((error instanceof Error ? error.message : '') ||'Error updating environment');
    }
  };

  // Get the selected client object
  const selectedClient = accessibleClients.find(c => c.id === selectedClientId);

  // Get the external ID based on user role
  const getExternalId = () => {
    // For partners with a selected client, use the selected client's ID
    if (isPartner && selectedClientId) {
      return selectedClientId;
    }

    // For client users, use their client_id from the user object
    if (user?.client_id) {
      return user?.client_id ?? '';
    }

    // Fallback
    return '';
  };

  // Filter environments by global selected client for partners
  const getFilteredEnvironments = () => {
    if (!isPartner || !selectedClientId) {
      return environments;
    }
    return environments.filter(env => env.client_id === selectedClientId);
  };

  // Filter accounts by global selected client for partners
  const getFilteredAccounts = () => {
    if (!isPartner || !selectedClientId) {
      return accounts;
    }
    return accounts.filter(acc => acc.client_id === selectedClientId || ['payer', 'partner'].includes(acc.account_type ?? ""));
  };

  const filteredEnvironments = getFilteredEnvironments();
  const filteredAccounts = getFilteredAccounts();

  const renderWizardStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Step 1: Select or Add AWS Account</h3>

      {accounts.length > 0 && (
        <div>
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Existing Accounts</span>
          <div className="space-y-2">
            {accounts.map(account => (
              <button
                key={account.id}
                onClick={() => {
                  setSelectedAccount(account);
                  setWizardStep(3);
                }}
                className="w-full text-left p-4 border dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-gray-700"
              >
                <div className="font-medium text-gray-900 dark:text-white">Account: {account.account_id}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Role: {account.role_name}</div>
              </button>
            ))}
          </div>

          <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">or</div>
        </div>
      )}

      <button
        onClick={() => setWizardStep(2)}
        className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
      >
        + Add New AWS Account
      </button>
    </div>
  );

  const renderWizardStep2 = () => {
    // Select appropriate policy based on account type
    let policyToUse;
    let policyLabel;
    const isPayer = accountType === 'payer';
    const isPartnerAccount = accountType === 'partner';

    if (accountType === 'payer') {
      policyToUse = awsPayerAccountPolicy;
      policyLabel = '(Billing Access Only)';
    } else if (accountType === 'partner') {
      policyToUse = awsPartnerCentralPolicy;
      policyLabel = '(Partner Central Read-Only)';
    } else {
      policyToUse = awsCrossAccountPolicy;
      policyLabel = '';
    }

    // Debug logging to verify policy selection

    let createAccountButtonLabel: string;
    if (isPayer) {
      createAccountButtonLabel = 'Add Payer Account';
    } else if (isPartnerAccount) {
      createAccountButtonLabel = 'Add Partner Account';
    } else if (showAccountOnlyWizard) {
      createAccountButtonLabel = 'Add Account';
    } else {
      createAccountButtonLabel = 'Continue to Environment Setup';
    }

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{showAccountOnlyWizard ? 'Configure AWS Cross-Account Access' : 'Step 2: Configure AWS Cross-Account Access'}</h3>

        {/* Account Type Selection */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <span className="block text-sm font-medium text-gray-900 dark:text-white mb-3">Account Type</span>
          <div className="space-y-3">
            <label aria-label="Standard Account" className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-white dark:hover:bg-gray-700 transition-colors"
                   style={{ borderColor: accountType === 'standard' ? '#3b82f6' : 'transparent' }}>
              <input
                type="radio"
                name="accountType"
                value="standard"
                checked={accountType === 'standard'}
                onChange={(e) => setAccountType(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">Standard Account</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Child/member account or standalone AWS account with full infrastructure access
                </div>
              </div>
            </label>

            <span className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-white dark:hover:bg-gray-700 transition-colors"
                   style={{ borderColor: accountType === 'payer' ? '#3b82f6' : 'transparent' }}>
              <input
                type="radio"
                name="accountType"
                value="payer"
                checked={accountType === 'payer'}
                onChange={(e) => setAccountType(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">Payer/Master Account</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Billing account with organization-wide cost visibility and credits tracking
                </div>
              </div>
            </span>

            {isPartner && (
              <label aria-label="Partner Account" className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-white dark:hover:bg-gray-700 transition-colors"
                     style={{ borderColor: accountType === 'partner' ? '#3b82f6' : 'transparent' }}>
                <input
                  type="radio"
                  name="accountType"
                  value="partner"
                  checked={accountType === 'partner'}
                  onChange={(e) => setAccountType(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">Partner Account</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Partner Central AWS account for accessing funding requests and credit allocations
                  </div>
                </div>
              </label>
            )}
          </div>
        </div>

        {/* Parent Payer Account Selection - Only show for standard accounts */}
        {accountType === 'standard' && accounts.filter(acc => acc.account_type === 'payer').length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <label htmlFor="link-payer-account" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Link to Payer Account (Optional)
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              If this account is part of an AWS Organization, select the payer/master account to enable organization-wide credits tracking
            </p>
            <select
                                                    id="link-payer-account"
              value={parentPayerAccountId}
              onChange={(e) => setParentPayerAccountId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
            >
              <option value="">No payer account (standalone)</option>
              {accounts.filter(acc => acc.account_type === 'payer').map(account => (
                <option key={account.id} value={account.id}>
                  Account {account.account_id}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
            Setup Instructions {isPayer && '(Billing Access Only)'}{isPartnerAccount && ' (Partner Account)'}
          </h4>
        {isPartner && selectedClient && accountType === 'standard' && (
          <div className="mb-3 p-2 bg-white dark:bg-gray-800 rounded border border-blue-300 dark:border-blue-700">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Configuring for client: <span className="text-blue-600 dark:text-blue-400">{selectedClient.name}</span>
            </span>
          </div>
        )}
        {isPartnerAccount && (
          <div className="mb-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-300 dark:border-purple-700">
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
              <FiInfo className="inline w-4 h-4 mr-1 align-text-bottom" />Partner accounts are organization-level and will access funding data for all your clients
            </span>
          </div>
        )}
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li>Log in to your AWS account</li>
          <li>Navigate to IAM → Roles → Create Role</li>
          <li>Select "AWS Account" as the trusted entity type</li>
          <li className="flex items-center gap-2">
            <span>Enter Aivilo's AWS Account ID: <code className="bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-900 dark:text-gray-100">{aiviloAccountId}</code></span>
            <button
              onClick={() => navigator.clipboard.writeText(aiviloAccountId)}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              title="Copy Account ID"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </li>
          <li>
            <div>Check "Require external ID" and enter:</div>
            <div className="flex items-center gap-2 mt-1">
              <code className="bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-900 dark:text-gray-100 text-xs break-all flex-1">{getExternalId()}</code>
              <button
                onClick={() => navigator.clipboard.writeText(getExternalId())}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex-shrink-0"
                title="Copy External ID"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </li>
          <li>Skip adding permissions during role creation (click Next)</li>
          <li className="flex items-center gap-2">
            <span>Name the role: <code className="bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-900 dark:text-gray-100">AiviloLensCrossAccountRole</code></span>
            <button
              onClick={() => navigator.clipboard.writeText('AiviloLensCrossAccountRole')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              title="Copy Role Name"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </li>
          <li>Complete the role creation</li>
          <li>After the role is created, open the role and go to the "Permissions" tab</li>
          <li>Click "Add permissions" → "Create inline policy" → Switch to "JSON" tab</li>
          <li>
            Paste the policy JSON below, name the policy{' '}
            <code className="bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-900 dark:text-gray-100">AiviloPolicy</code>
            <button
              onClick={() => navigator.clipboard.writeText('AiviloPolicy')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 inline-flex align-middle ml-1"
              title="Copy Policy Name"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>, and save the inline policy
          </li>
        </ol>
      </div>

      <div className="bg-gray-900 p-4 rounded-lg overflow-auto">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs text-gray-400">
            IAM Policy JSON {policyLabel}
          </span>
          <button
            onClick={() => navigator.clipboard.writeText(JSON.stringify(policyToUse, null, 2))}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            Copy
          </button>
        </div>
        <pre className="text-xs text-gray-300 overflow-auto">
          {JSON.stringify(policyToUse, null, 2)}
        </pre>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="aws-account-id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">AWS Account ID</label>
          <input
                                                    id="aws-account-id"
            type="text"
            value={newAccountId}
            onChange={(e) => setNewAccountId(e.target.value)}
            placeholder="123456789012"
            maxLength={12}
            className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
        </div>

        <button
          onClick={testAccountAccess}
          disabled={!newAccountId || testing}
          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </button>

        {testResult && (
          <div ref={testResultRef} className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'}`}>
            <div className="font-medium">{testResult.success ? <><FiCheck className="inline w-4 h-4 mr-1" />Success</> : <><FiXCircle className="inline w-4 h-4 mr-1" />Failed</>}</div>
            <div className="text-sm mt-1">{testResult.message}</div>
            {testResult.account_id && (
              <div className="text-xs mt-2">Account: {testResult.account_id}</div>
            )}
            {!testResult.success && (
              <div className="text-sm mt-3 pt-3 border-t border-red-200 dark:border-red-800">
                <span className="font-medium">Need help?</span> You can create a support ticket from the Support page, or reach out to us at{' '}
                <a href="mailto:support@aivilo.ai" className="underline hover:no-underline">support@aivilo.ai</a>
              </div>
            )}
          </div>
        )}

        {testResult?.success && (
          <button
            onClick={createAccount}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {createAccountButtonLabel}
          </button>
        )}
      </div>

      {!showAccountOnlyWizard && (
        <button
          onClick={() => setWizardStep(1)}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          ← Back
        </button>
      )}
    </div>
    );
  };

  const renderWizardStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Step 3: Create Environment</h3>

      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
        <div className="text-sm text-gray-600 dark:text-gray-400">Selected Account</div>
        <div className="font-medium text-gray-900 dark:text-white">{selectedAccount?.account_id}</div>
      </div>

      <div>
        <label htmlFor="env-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Environment Name *</label>
        <input
                                                    id="env-name"
          type="text"
          value={environmentName}
          onChange={(e) => setEnvironmentName(e.target.value)}
          placeholder="e.g., Production, Development"
          className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
        />
      </div>

      <div>
        <label htmlFor="env-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Environment Type</label>
        <select
                                                    id="env-type"
          value={environmentType}
          onChange={(e) => setEnvironmentType(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="">Select type...</option>
          <option value="production">Production</option>
          <option value="staging">Staging</option>
          <option value="development">Development</option>
          <option value="testing">Testing</option>
        </select>
      </div>

      <div>
        <label htmlFor="env-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
        <textarea
                                                    id="env-description"
          value={environmentDescription}
          onChange={(e) => setEnvironmentDescription(e.target.value)}
          rows={3}
          placeholder="Describe this environment..."
          className="w-full px-3 py-2 border rounded-lg bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
        />
      </div>

      {/* Track AWS Credits Checkbox */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <label className="flex items-start gap-3 cursor-pointer" aria-label="Track AWS Credits">
          <input
            type="checkbox"
            checked={trackCredits}
            onChange={(e) => setTrackCredits(e.target.checked)}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="font-medium text-gray-900 dark:text-white">Track AWS Credits</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Enable tracking of AWS credits for this environment. We'll automatically fetch credit usage
              and remaining balance from AWS Cost Explorer.
            </div>
          </div>
        </label>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={() => setWizardStep(1)}
          className="flex-1 px-4 py-2 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
        >
          Back
        </button>
        <button
          onClick={createEnvironment}
          disabled={!environmentName}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Create Environment
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AWS Environments</h1>
          {!isReadOnly && (
            <div className="flex items-center gap-2">
              <div className="relative group">
                <button
                  onClick={() => {
                    if (!isDemoMode) {
                      resetWizard();
                      setShowAccountOnlyWizard(true);
                    }
                  }}
                  disabled={isDemoMode}
                  className={`px-4 py-2 rounded-lg text-white ${isDemoMode ? 'bg-green-400 cursor-not-allowed opacity-60' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  + Add AWS Account
                </button>
                {isDemoMode && (
                  <div className="absolute right-0 top-full mt-1 z-10 w-56 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {isOnboarding
                      ? 'Subscribe to add AWS accounts.'
                      : 'Log in to add AWS accounts.'}
                  </div>
                )}
              </div>
              <div className="relative group">
                <button
                  onClick={() => {
                    if (!isDemoMode) {
                      resetWizard();
                      setShowWizard(true);
                    }
                  }}
                  disabled={isDemoMode}
                  className={`px-4 py-2 rounded-lg text-white ${isDemoMode ? 'bg-blue-400 cursor-not-allowed opacity-60' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  + Add Environment
                </button>
                {isDemoMode && (
                  <div className="absolute right-0 top-full mt-1 z-10 w-56 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {isOnboarding
                      ? 'Subscribe to add AWS environments.'
                      : 'Log in to add AWS environments.'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add AWS Environment</h2>
              <button
                onClick={() => {
                  setShowWizard(false);
                  resetWizard();
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {wizardStep === 1 && renderWizardStep1()}
            {wizardStep === 2 && renderWizardStep2()}
            {wizardStep === 3 && renderWizardStep3()}
          </div>
        </div>
      )}

      {/* Account-Only Wizard Modal */}
      {showAccountOnlyWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add AWS Account</h2>
              <button
                onClick={() => {
                  setShowAccountOnlyWizard(false);
                  resetWizard();
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            {renderWizardStep2()}
          </div>
        </div>
      )}

      {/* AWS Accounts Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">AWS Accounts</h2>
        {filteredAccounts.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 mb-3">
              No AWS accounts configured
            </div>
            {!isReadOnly && !isDemoMode && (
              <button
                onClick={() => {
                  resetWizard();
                  setShowAccountOnlyWizard(true);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                + Add AWS Account
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredAccounts.map(account => (
              <div key={account.id} className="border rounded-lg p-4 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-lg text-gray-900 dark:text-white">
                        Account ID: {account.account_id}
                      </h3>
                      {account.account_type === 'payer' && (
                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded text-xs font-medium">
                          Payer Account
                        </span>
                      )}
                      {account.account_type === 'partner' && (
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs font-medium">
                          Partner Central
                        </span>
                      )}
                    </div>
                    <div className="flex items-center flex-wrap gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                      <span>Role: {account.role_name}</span>
                      {account.account_type !== 'payer' && account.account_type !== 'partner' && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                          {environments.filter(env => env.aws_account_id === account.id).length} environment(s)
                        </span>
                      )}
                    </div>

                    {/* Show parent payer account link info */}
                    {account.account_type === 'standard' && account.parent_payer_account_id && (
                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Linked to payer:</span>{' '}
                        {accounts.find(a => a.id === account.parent_payer_account_id)?.account_id || 'Unknown'}
                      </div>
                    )}
                  </div>
                  {!isReadOnly && (
                    <div className="flex gap-2">
                      {/* Edit button - only for standard accounts */}
                      {account.account_type === 'standard' && (
                        <button
                          onClick={() => startEditAccount(account)}
                          className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                          title="Edit account"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => deleteAccount(account.id)}
                        className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Delete account"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                {/* Edit form - show when this account is being edited */}
                {editingAccountId === account.id && (
                  <div className="mt-4 pt-4 border-t dark:border-gray-700">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Link to Payer Account</h4>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <label htmlFor="edit-payer-account" className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                          Select parent payer account (optional)
                        </label>
                        <select
                                                    id="edit-payer-account"
                          value={editPayerAccountId}
                          onChange={(e) => setEditPayerAccountId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="">No payer account (standalone)</option>
                          {accounts
                            .filter(acc => acc.account_type === 'payer' && acc.id !== account.id)
                            .map(payerAccount => (
                              <option key={payerAccount.id} value={payerAccount.id}>
                                Account {payerAccount.account_id} (Payer)
                              </option>
                            ))}
                        </select>
                        {accounts.filter(acc => acc.account_type === 'payer').length === 0 && (
                          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            No payer accounts configured. Add a payer account first to enable linking.
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => updateAccount(account.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditAccount}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Environments List */}
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Environments</h2>
      {filteredEnvironments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-gray-500 dark:text-gray-400 mb-4">
            {environments.length === 0
              ? 'No AWS environments configured'
              : 'No environments found for the selected client'}
          </div>
          {!isReadOnly && environments.length === 0 && (
            <div className="relative group inline-block">
              <button
                onClick={() => {
                  if (!isDemoMode) {
                    resetWizard();
                    setShowWizard(true);
                  }
                }}
                disabled={isDemoMode}
                className={`px-4 py-2 rounded-lg text-white ${isDemoMode ? 'bg-blue-400 cursor-not-allowed opacity-60' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                Add Your First Environment
              </button>
              {isDemoMode && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-10 w-56 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {isOnboarding
                    ? 'Subscribe to add AWS environments.'
                    : 'Log in to add AWS environments.'}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredEnvironments.map(env => (
            <div key={env.id} className="border rounded-lg p-4 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-lg text-gray-900 dark:text-white">{env.name}</h3>
                  {env.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{env.description}</p>
                  )}
                  <div className="flex items-center flex-wrap gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {isPartner && env.client_name && (
                      <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded">
                        Client: {env.client_name}
                      </span>
                    )}
                    <span>Account: {env.aws_account?.account_id}</span>
                    {env.environment_type && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                        {env.environment_type}
                      </span>
                    )}
                    {env.track_credits && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">
                        Credits Tracking Enabled
                      </span>
                    )}
                  </div>
                </div>
                {!isReadOnly && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEditEnvironment(env)}
                      className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                      title="Edit environment"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => deleteEnvironment(env.id)}
                      className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      title="Delete environment"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Edit form - show when this environment is being edited */}
              {editingEnvironmentId === env.id && (
                <div className="mt-4 pt-4 border-t dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Edit Environment</h4>
                  <div className="space-y-4">
                    {/* AWS Account Info */}
                    {env.aws_account && (
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Linked AWS Account
                            </span>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono font-semibold text-gray-900 dark:text-white">
                                  {env.aws_account.account_id}
                                </span>
                                {env.aws_account.account_type === 'payer' && (
                                  <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs rounded">
                                    Payer Account
                                  </span>
                                )}
                                {env.aws_account.account_type === 'partner' && (
                                  <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                                    Partner Central
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Role: {env.aws_account.role_name || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        {env.aws_account?.parent_payer_account_id && (
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Linked to payer: {accounts.find(a => a.id === env.aws_account?.parent_payer_account_id)?.account_id || env.aws_account?.parent_payer_account_id}
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label htmlFor="env-name" className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Environment Name *
                      </label>
                      <input
                                                    id="env-name"
                        type="text"
                        value={editEnvName}
                        onChange={(e) => setEditEnvName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., Production, Development"
                      />
                    </div>
                    <div>
                      <label htmlFor="env-type" className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Environment Type
                      </label>
                      <input
                                                    id="env-type"
                        type="text"
                        value={editEnvType}
                        onChange={(e) => setEditEnvType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., production, staging, development"
                      />
                    </div>
                    <div>
                      <label htmlFor="env-description" className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Description
                      </label>
                      <textarea
                                                    id="env-description"
                        value={editEnvDescription}
                        onChange={(e) => setEditEnvDescription(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                        placeholder="Brief description of this environment"
                      />
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                      <label className="flex items-start gap-3 cursor-pointer" aria-label="Track AWS Credits">
                        <input
                          type="checkbox"
                          checked={editEnvTrackCredits}
                          onChange={(e) => setEditEnvTrackCredits(e.target.checked)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">Track AWS Credits</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Enable tracking of AWS credits for this environment
                          </div>
                        </div>
                      </label>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => updateEnvironment(env.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditEnvironment}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AWSEnvironments;
